import React, { useCallback, useState } from 'react';
import { SettingsFormModel, UseStateModel } from '@features/ControlView/sections/Tabs/ControlTabs.models';
import {
  Button,
  ConfirmButton,
  CustomInput,
  DataTable,
  DataTableColumn,
  PromptType,
  useNotifications,
} from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { Typography } from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import { FlowUnitsOptionsFn, PegasusFlowStatusesModel, PressureUnitsOptionsFn } from '@shared/models';
import { useFormikContext } from 'formik';
import { useAppSelector } from '@core/store/store';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import {
  PEGASUS_CUSTOM_ROW_HEIGHT,
  PEGASUS_FREE_COL_MIN_WIDTH,
  PEGASUS_HEADER_HEIGHT,
  PEGASUS_MAX_HEIGHT,
  pegasusFlowTableColumns,
} from './PegasusTables.utils';
import { indexOf, isEmpty } from 'lodash';

export interface PegasusFlowTableProps extends UseStateModel<PegasusFlowStatusesModel[]> {
  disableInputs?: boolean;
  editMode?: boolean;
  maxHeight?: number;
  /**
   * `dataTestId` - name of a pegasus flow table (preferably `<name>-pegasus-flow-table`)
   * @example
   * ```tsx
   * <PegasusFlowTable {...props} dataTestId="test-pegasus-flow-table" />
   */
  dataTestId: string;
}

export const PegasusFlowTable: React.FC<PegasusFlowTableProps> = ({
  data,
  setData,
  disableInputs = false,
  editMode = false,
  maxHeight = PEGASUS_MAX_HEIGHT,
  dataTestId,
}) => {
  const { values } = useFormikContext<SettingsFormModel>() ?? {
    values: {} as SettingsFormModel,
  };

  const [flow, setFlow] = useState('');
  const [pressure, setPressure] = useState('');

  const { preferences: units } = useAppSelector(preferencesSelector);

  const { handleError } = useNotifications();

  const handleSortTable = useCallback((a: PegasusFlowStatusesModel, b: PegasusFlowStatusesModel) => {
    const flowOrderIndicator = Number(a.flow) - Number(b.flow);
    const pressureOrderIndicator = Number(a.pressure) - Number(b.pressure);

    return flowOrderIndicator === 0 ? pressureOrderIndicator : flowOrderIndicator;
  }, []);

  const handleAddingFlow = useCallback(
    (flowValue: string, pressureValue: string) => {
      const validData =
        flowValue &&
        pressureValue &&
        Number.isFinite(Number(flowValue)) &&
        Number.isFinite(Number(pressureValue)) &&
        Number(flowValue) > 0 &&
        Number(pressureValue) >= 0 &&
        (!flowValue.includes('.') || flowValue.split('.')[1].length <= 2) &&
        (!pressureValue.includes('.') || pressureValue.split('.')[1].length <= 2);

      if (validData) {
        const dataToAdd = {
          flow: flowValue,
          uiFlow: Number(flowValue).toFixed(2),
          pressure: pressureValue,
          uiPressure: Number(pressureValue).toFixed(2),
        } as PegasusFlowStatusesModel;

        const exists = data.some((el) => el.flow === dataToAdd.flow);

        if (exists) {
          handleError(t`Cannot add new row to Flow table. Flow cannot be the same in the table`, t`FLOW TABLE`);
        } else {
          if (
            (!values.actuatorMin || Number(dataToAdd.pressure) >= values.actuatorMin) &&
            (!values.actuatorMax || Number(dataToAdd.pressure) <= values.actuatorMax)
          ) {
            setData((prev) => [...prev, dataToAdd]);
            setFlow('');
            setPressure('');
          } else {
            handleError(t`Your table values must be between your actuator range`, t`FLOW TABLE`);
          }
        }
      } else {
        handleError(
          t`Cannot add new row to Flow table. Flow and Pressure should be positive numbers with at most 2 decimal places`,
          t`FLOW TABLE`
        );
      }
    },
    [handleError, setData, data, values.actuatorMax, values.actuatorMin]
  );

  return (
    <>
      <div className="grid" data-testid={`${dataTestId}-wrapper`}>
        <DataTable
          columns={[
            ...pegasusFlowTableColumns(
              FlowUnitsOptionsFn()[units.flowUnit],
              PressureUnitsOptionsFn()[units.pressureUnit]
            ),
            ...(editMode
              ? [
                  {
                    Header: '',
                    accessor: 'action' as any,
                    fixed: 'right',
                    width: 40,
                    renderCell: ({ data: row, dataTestId }) => (
                      <RemoveCircle
                        color="primary"
                        className="z-10 cursor-pointer"
                        onClick={() => {
                          const id = indexOf(data, row);

                          setData((prev) => prev.filter((_, i) => id !== i));
                        }}
                        data-testid={`${dataTestId}-remove-row-button`}
                      />
                    ),
                  } as DataTableColumn<PegasusFlowStatusesModel>,
                ]
              : []),
          ]}
          data={[...data]?.sort(handleSortTable) ?? []}
          freeColMinWidth={PEGASUS_FREE_COL_MIN_WIDTH}
          customRowHeight={PEGASUS_CUSTOM_ROW_HEIGHT}
          customHeaderHeight={PEGASUS_HEADER_HEIGHT}
          heightToContent
          maxHeight={maxHeight}
          dataTestId={`${dataTestId}`}
        />
      </div>

      {editMode && Boolean(data) && !isEmpty(data) ? (
        <ConfirmButton
          promptProps={{
            content: (
              <div className="space-y-1">
                <Typography>
                  <Trans>Are you sure you want to clear the current table?</Trans>
                </Typography>

                <Typography variant="body2" className="text-center italic">
                  <Trans>In order to use the table you need to Submit Control Settings</Trans>
                </Typography>
              </div>
            ),
            title: t`Clear Flow Table`,
            type: PromptType.Warning,
            onConfirm: () => setData([]),
            dataTestId: `${dataTestId}-clear-confirm-prompt`,
          }}
        >
          {({ onClick }) => (
            <Button
              style={{ height: 32, marginLeft: 'auto', padding: 8 }}
              variant="text"
              endIcon={<RemoveCircle color="primary" />}
              onClick={onClick}
              dataTestId={`${dataTestId}-clear-confirm-prompt-button`}
            >
              <Trans>Clear Flow Table</Trans>
            </Button>
          )}
        </ConfirmButton>
      ) : null}

      {editMode && (
        <div data-testid={`${dataTestId}-add-new-entry-wrapper`}>
          <Typography>
            <Trans>Add a new entry</Trans>:
          </Typography>

          <DataTable
            columns={[
              {
                Header: t`Flow`,
                accessor: 'flow',
                width: 124,
                renderCell: ({ dataTestId }) => (
                  <CustomInput
                    name="flow"
                    type="flow"
                    value={flow}
                    minWidth={100}
                    maxWidth={'100%'}
                    onChange={(e) => setFlow(e.target.value)}
                    disabled={disableInputs}
                    onEnterPress={() => handleAddingFlow(flow, pressure)}
                    customBorderColor="#cccccc"
                    dataTestId={`${dataTestId}-flow-input`}
                  />
                ),
              },
              {
                Header: t`Pressure`,
                accessor: 'pressure',
                renderCell: ({ dataTestId }) => (
                  <CustomInput
                    name="pressure"
                    value={pressure}
                    minWidth={100}
                    maxWidth={'100%'}
                    onChange={(e) => setPressure(e.target.value)}
                    disabled={disableInputs}
                    onEnterPress={() => handleAddingFlow(flow, pressure)}
                    customBorderColor="#cccccc"
                    dataTestId={`${dataTestId}-pressure-input`}
                  />
                ),
              },
              {
                Header: '',
                accessor: 'action' as any,
                fixed: 'right',
                width: 40,
                renderCell: ({ dataTestId }) => (
                  <AddCircle
                    color="primary"
                    className="z-10 cursor-pointer"
                    onClick={() => {
                      handleAddingFlow(flow, pressure);
                    }}
                    data-testid={`${dataTestId}-add-row-button`}
                  />
                ),
              },
            ]}
            data={[{ flow, pressure }]}
            customRowHeight={PEGASUS_CUSTOM_ROW_HEIGHT + 8}
            freeColMinWidth={PEGASUS_FREE_COL_MIN_WIDTH}
            customHeaderHeight={PEGASUS_HEADER_HEIGHT}
            heightToContent
            dataTestId={`${dataTestId}-add-new-entry-table`}
          />
        </div>
      )}
    </>
  );
};
